import { useDeferredValue, useMemo, useState } from 'react';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellBorderWidthEnum, ModularTable, Pagination, SelectOption, SortEnum, TableColorThemeEnum, TextField, useViewport } from '@tryhackme/thm-ui-components';
import { useTheme } from 'styled-components';
import { SelectOptions, defaultOption, validSortValues } from '../../corporate.constants';
import { formatCompanyData, sortData } from '../../corporate.helpers';
import { HeadingWrapper, StyledDropDown, StyledFilters, StyledHeading, StyledNoRecordMessage, StyledPagination, StyledSearch, StyledSelect, StyledSize, StyledTabHead, StyledText, UsersInfo, Wrapper, StyledEmptySeats, StyledDownload, StyledFontAwesomeIcon, StyledSubscriberField } from '../../corporate.styles';
import { AddSeatModal } from '../add-seat-modal';
import { AddInstructorModal } from '../add-instructor-modal';
import { CompanyInstructorItems, CompanySubscriberItems, CorporateProps, UserTableType } from '../../corporate.types';
export const UserTable = <T extends CompanySubscriberItems | CompanyInstructorItems,>({
  title,
  columns,
  companyDetails,
  data,
  icon,
  setFeedbackParams,
  isSubscriber,
  feedbackParams,
  totalUsersCount
}: CorporateProps<T>) => {
  const theme = useTheme();
  const {
    width
  } = useViewport();
  const deferredFeedbackParams = useDeferredValue(feedbackParams);
  const [searchValue, setSearchValue] = useState<string>('');
  const [activeSortColumn, setActiveSortColumn] = useState<UserTableType<T>['activeSortColumn']>(null);
  const [selectedOption, setSelectedOption] = useState<SelectOption>({
    id: '1',
    value: '10',
    label: '10'
  });
  const numberOfRecords = {
    total: data?.totalDocs ?? '0',
    fetched: data?.users?.length ?? 0
  };
  let formattedRows: UserTableType<T>['rows'] = [];
  const formattedSortableColumn = columns.map(col => {
    const obj = {
      ...col
    };
    if (obj.sortFunction) {
      obj.sortFunction = () => sortData(setActiveSortColumn, setFeedbackParams, obj.key);
    }
    return obj;
  });
  formattedRows = useMemo(() => {
    if (data?.users) {
      return ((isSubscriber ? formatCompanyData((data.users as CompanySubscriberItems[]) ?? []) : data.users) as T[]);
    }
    return [];
  }, [data?.users, isSubscriber]);
  const handlePageChange = (pageNumber: number) => {
    setFeedbackParams(prevParams => ({
      ...prevParams,
      page: pageNumber
    }));
  };
  const handleSelection = (option: SelectOption = defaultOption) => {
    setSelectedOption(() => ({
      value: option.value,
      label: option.label,
      id: option.id
    }));
    setFeedbackParams(prevParams => ({
      ...prevParams,
      limit: Number.parseInt(option.value, 10),
      page: 1
    }));
  };
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackParams(prevParams => ({
      ...prevParams,
      search: e.target.value,
      page: 1
    }));
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    handleTextChange(e);
  };
  return <UsersInfo data-sentry-element="UsersInfo" data-sentry-component="UserTable" data-sentry-source-file="user-table.tsx">
      <HeadingWrapper data-sentry-element="HeadingWrapper" data-sentry-source-file="user-table.tsx">
        <StyledTabHead data-sentry-element="StyledTabHead" data-sentry-source-file="user-table.tsx">
          <StyledFontAwesomeIcon icon={icon} data-sentry-element="StyledFontAwesomeIcon" data-sentry-source-file="user-table.tsx" />
          <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="user-table.tsx">{title}</StyledHeading>
        </StyledTabHead>
      </HeadingWrapper>
      <StyledFilters data-sentry-element="StyledFilters" data-sentry-source-file="user-table.tsx">
        {isSubscriber ? <StyledSearch>
            <StyledSubscriberField placeholder={`Search for ${title}`} variant="input" leftAdornment={<FontAwesomeIcon icon={faSearch} />} onChange={handleInputChange} value={searchValue} />
            {isSubscriber && width <= Number.parseInt(theme.sizes.md, 10) && <StyledEmptySeats>{totalUsersCount}</StyledEmptySeats>}
          </StyledSearch> : <StyledSubscriberField placeholder={`Search for ${title}`} variant="input" leftAdornment={<FontAwesomeIcon icon={faSearch} />} onChange={handleInputChange} value={searchValue} />}
        <StyledDropDown data-sentry-element="StyledDropDown" data-sentry-source-file="user-table.tsx">
          {isSubscriber && width > Number.parseInt(theme.sizes.md, 10) && <StyledEmptySeats>{totalUsersCount}</StyledEmptySeats>}
          <StyledDownload data-sentry-element="StyledDownload" data-sentry-source-file="user-table.tsx">
            {isSubscriber ? <AddSeatModal companyDetails={companyDetails} /> : <AddInstructorModal width="17" companyId={companyDetails._id} />}
          </StyledDownload>
          <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="user-table.tsx">Show</StyledSize>
          <StyledSelect data-sentry-element="StyledSelect" data-sentry-source-file="user-table.tsx">
            <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={handleSelection} options={SelectOptions} selected={selectedOption} defaultValue={SelectOptions[0]} data-sentry-element="TextField" data-sentry-source-file="user-table.tsx" />
          </StyledSelect>
          <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="user-table.tsx">records</StyledSize>
        </StyledDropDown>
      </StyledFilters>
      <ModularTable isLoading={false} activeSortColumn={activeSortColumn} activeSortOrder={validSortValues.includes(deferredFeedbackParams.sort) ? SortEnum.descending : SortEnum.ascending} rows={formattedRows} columns={formattedSortableColumn} colorTheme={TableColorThemeEnum.grayWhite} cellBorderWidth={CellBorderWidthEnum.thick} rowHeight="6.4rem" data-sentry-element="ModularTable" data-sentry-source-file="user-table.tsx" />
      {numberOfRecords.fetched === 0 && <StyledNoRecordMessage>No feedback found</StyledNoRecordMessage>}

      <Wrapper data-sentry-element="Wrapper" data-sentry-source-file="user-table.tsx">
        <StyledText data-sentry-element="StyledText" data-sentry-source-file="user-table.tsx">
          <p>
            Showing{' '}
            {numberOfRecords.fetched > 0 ? `${(deferredFeedbackParams.page - 1) * deferredFeedbackParams.limit + 1} to ` : 0}
            {numberOfRecords.fetched > 0 ? `${deferredFeedbackParams.page * deferredFeedbackParams.limit} ` : ` `}
            of {numberOfRecords.total} records
          </p>
        </StyledText>
        {numberOfRecords.fetched > 0 && <StyledPagination>
            <Pagination onChange={handlePageChange} selectedPage={deferredFeedbackParams.page} totalPages={Math.ceil(Number.parseInt(numberOfRecords.total, 10) / deferredFeedbackParams.limit)} />
          </StyledPagination>}
      </Wrapper>
    </UsersInfo>;
};